import { Component, inject, OnDestroy, Signal } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { QuestionnaireConfirmationData } from '../models/questionnaire-confirmation-data.model';
import { ButtonModule } from '@shared/components/atoms/button/button.module';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { SnackBarService } from '@core/services/snackbar.service';
import { ConsentQuestionnairesService } from '@core/services/consent/consent-questionnaires.service';
import { ConsentUrlsAdapted } from '@core/data/consent-urls/models/consent-urls-adapted.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConsentUrlsService } from '@core/data/consent-urls/services/consent-urls.service';
import { QuestionnaireDialogService } from '@features/questionnaire-renderer/questionnaire-dialog.service';
import { StepperDialogComponent } from '@shared/components/dialogs/stepper-dialog/stepper-dialog.component';

@Component({
  selector: 'xt-questionnaire-confirmation-dialog',
  standalone: true,
  imports: [
    TranslocoModule,
    ButtonModule,
    CypressDirectiveModule,
    StepperDialogComponent
],
  templateUrl: './questionnaire-share-confirmation-dialog.component.html',
  styleUrls: ['./questionnaire-share-confirmation-dialog.component.scss'],
})
export class QuestionnaireShareConfirmationDialogComponent
  implements OnDestroy
{
  private readonly data =
    inject<QuestionnaireConfirmationData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<QuestionnaireShareConfirmationDialogComponent>,
  );
  private readonly consentQuestionnaireService = inject(
    ConsentQuestionnairesService,
  );
  private readonly consentUrlsService = inject(ConsentUrlsService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly questionnaireDialogService = inject(
    QuestionnaireDialogService,
  );
  private readonly translocoService = inject(TranslocoService);

  dialogConfig = { title: 'app.dialog.addDocument.consent' };

  link: string =
    ' ' +
    '<a href="/dashboard/privacy-cockpit" class="blue-link">' +
    this.translocoService.translate(
      'app.privacyCockpit.consentDialog.manageSharing',
    ) +
    '</a>' +
    ' ';

  protected readonly currentStep = this.questionnaireDialogService.currentStep;
  protected readonly stepCount = this.questionnaireDialogService.stepCount;

  public consents: Signal<ConsentUrlsAdapted> = toSignal(
    this.consentUrlsService.consents$,
  );

  ngOnDestroy(): void {
    this.questionnaireDialogService.resetCurrentStep();
  }

  async shareQuestionnaire() {
    let hasErrors = false;

    for (const facility of this.data.providersToShareWith) {
      this.consentQuestionnaireService.init(facility.identifier);

      try {
        await this.consentQuestionnaireService
          .addConsent$([
            {
              resourceType: 'ServiceRequest',
              id: this.data.serviceRequestId,
              practitionerId: facility.identifier,
            },
          ])
          .toPromise();
      } catch (error) {
        console.error(`Failed to share with ${facility.identifier}`, error);
        hasErrors = true;
      }
    }

    if (hasErrors) {
      this.snackBarService.error(`app.questionnaire.share.error`);
    } else {
      this.snackBarService.success(`app.questionnaire.share.success`);
    }

    this.dialogRef.close();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
